import axios from "axios";
import { API_URL } from "./config";
import Helper from "./helpers";

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB max

const compressImage = async (file, maxWidth = 2048, quality = 0.85) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          }));
        }, 'image/jpeg', quality);
      };
    };
  });
};

const uploadWithRetry = async (url, payload, headers, timeout, onProgress, retryCount = 0) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const response = await axios.post(url, payload, {
      headers,
      timeout,
      signal: controller.signal,
      onUploadProgress: (progressEvent) => {
        clearTimeout(timeoutId);
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        if (onProgress) {
          onProgress(progress);
        }
      },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
    
    return response.data;
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      console.log(`Upload attempt ${retryCount + 1} failed, retrying in ${RETRY_DELAY}ms...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
      return uploadWithRetry(url, payload, headers, timeout, onProgress, retryCount + 1);
    }
    throw error;
  }
};

const uploadLargeFile = async (file, onProgress) => {
  const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB
  
  let fileToUpload = file;
  
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
  }
  
  if (file.type.startsWith('image/') && file.size > COMPRESSION_THRESHOLD) {
    try {
      console.log('Compressing large image before upload...');
      if (onProgress) onProgress(5);
      
      const quality = file.size > 10 * 1024 * 1024 ? 0.7 : 0.85;
      const maxWidth = file.size > 10 * 1024 * 1024 ? 1920 : 2048;
      
      fileToUpload = await compressImage(file, maxWidth, quality);
      console.log(`Compressed from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`);
      
      if (onProgress) onProgress(10);
    } catch (compressionError) {
      console.warn('Image compression failed, uploading original:', compressionError);
      fileToUpload = file;
    }
  }
  
  const payload = new FormData();
  payload.append("file", fileToUpload);
  
  try {
    const token = Helper.getAuthToken();
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    
    const headers = {
      "vg-ua": "cms",
      ...(token ? { "vg-auth-token": token, "vg-timezone": timeZone } : {}),
      "vg-sid": Helper.getSessionId(),
    };
    
    const timeout = Math.max(300000, fileToUpload.size / 1000);
    
    const adjustedProgress = fileToUpload !== file 
      ? (progress) => onProgress(10 + (progress * 0.9))
      : onProgress;
    
    const response = await uploadWithRetry(
      `${API_URL}/api/upload/upload-image-not-thumb`,
      payload,
      headers,
      timeout,
      adjustedProgress
    );
    
    return response;
  } catch (error) {
    console.error("Upload failed after all retries:", error);
    
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      throw new Error('Upload timed out. Please try with a smaller file or check your connection.');
    }
    
    if (error.response?.status === 504) {
      throw new Error('Server timeout. Please try with a smaller file.');
    }
    
    throw error;
  }
};

export { uploadLargeFile };